// B-03 — Répertoire contacts.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { ListToolbar, Segments, ToolbarGrow, ToolbarSearch } from '../ui/ListToolbar';
import { FaBuilding, FaChevronDown, FaEnvelope, FaPhone, FaPlus, FaUser, FaUserGroup } from '../ui/FaIcons';
import { Spinner } from '../ui/Spinner';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import { useContacts } from '../../hooks/queries/useContacts';
import {
  CONTACT_CATEGORY_LABELS,
  type ContactCategory,
  type ContactKind,
  getContactDisplayName,
} from '../../services/contacts/contactService';
import { ContactEditModal } from './ContactEditModal';

import { ResizableColumns } from '../ui/ResizableColumns';
const CATEGORIES: ContactCategory[] = [
  'client', 'opposing_party', 'expert', 'notary', 'bailiff', 'magistrate', 'other',
];

const CATEGORY_HUE: Record<ContactCategory, string> = {
  client:         'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  opposing_party: 'bg-[#f7e9ee] text-[#a23b5f]',
  expert:         'bg-[#efebf6] text-[#674a96]',
  notary:         'bg-[rgba(201,168,76,0.14)] text-[#8a6d1f]',
  bailiff:        'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
  magistrate:     'bg-[#e9ecf7] text-[#3d4d8c]',
  other:          'bg-neutral-100 text-[#737373]',
};

export function ContactsListPage() {
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const canEdit = canAccessFeature(asCollaboratorRole(collaborator?.role), 'contacts_edit');

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<ContactKind | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ContactCategory[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: contacts, isLoading } = useContacts({
    search: search.trim() || undefined,
    kind: kindFilter,
    category: categoryFilter.length ? categoryFilter : undefined,
  });

  const visible = useMemo(() => contacts ?? [], [contacts]);

  return (
    <>
      <div className="main-container-gdm space-y-3">
        <ListToolbar>
          <ToolbarSearch value={search} onChange={setSearch} placeholder="Rechercher (nom, email, SIRET)…" className="w-[300px]" />
          <Segments
            label="Type de contact"
            value={kindFilter ?? 'all'}
            onChange={(v) => setKindFilter(v === 'all' ? null : v)}
            options={[
              { value: 'all', label: 'Tous' },
              { value: 'individual', label: <><FaUser className="h-3 w-3" aria-hidden /> Personnes</> },
              { value: 'corporate', label: <><FaBuilding className="h-3 w-3" aria-hidden /> Sociétés</> },
            ]}
          />
          <CategoryDropdown selected={categoryFilter} onChange={setCategoryFilter} />
          <ToolbarGrow />
          {canEdit && (
            <Button onClick={() => setCreateOpen(true)}>
              <FaPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Nouveau contact
            </Button>
          )}
        </ListToolbar>

        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-cime-text-muted">
            <FaUserGroup className="mb-3 h-7 w-7 text-neutral-300" aria-hidden />
            <p className="text-sm">Aucun contact ne correspond aux filtres.</p>
            {canEdit && (
              <Button variant="ghost" onClick={() => setCreateOpen(true)} className="mt-3">
                <FaPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Créer le premier contact
              </Button>
            )}
          </div>
        ) : (
          <div className="max-h-[calc(100vh-230px)] overflow-auto">
          <table className="cime-table text-[13px]">
            <ResizableColumns id="contacts" />
            <thead className="sticky top-0 z-[1]">
              <tr>
                <th className="text-left py-2 pl-[18px] pr-2.5">Nom</th>
                <th className="text-left px-2.5 py-2">Email</th>
                <th className="text-left px-2.5 py-2">Téléphone</th>
                <th className="text-left px-2.5 py-2">Ville</th>
                <th className="text-left px-2.5 py-2">Catégorie</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/contacts/${c.id}`)}
                  className="group cursor-pointer transition"
                >
                  <td className="py-[9px] pl-[18px] pr-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[rgba(4,52,41,0.07)] text-cime-primary">
                        {c.kind === 'corporate' ? (
                          <FaBuilding className="h-3 w-3" aria-hidden />
                        ) : (
                          <FaUser className="h-3 w-3" aria-hidden />
                        )}
                      </span>
                      <span className="truncate font-medium text-cime-text-primary group-hover:text-cime-primary">
                        {getContactDisplayName(c)}
                      </span>
                    </div>
                  </td>
                  <td className="px-2.5 py-[9px] text-cime-text-primary">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="group/lk inline-flex max-w-full items-center gap-1.5 hover:text-cime-primary hover:underline">
                        <FaEnvelope className="h-3 w-3 shrink-0 text-neutral-400 group-hover/lk:text-cime-primary" aria-hidden />
                        <span className="truncate">{c.email}</span>
                      </a>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-2.5 py-[9px] tabular-nums text-cime-text-primary">
                    {c.phone ? (
                      <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="group/lk inline-flex items-center gap-1.5 whitespace-nowrap hover:text-cime-primary hover:underline">
                        <FaPhone className="h-3 w-3 shrink-0 text-neutral-400 group-hover/lk:text-cime-primary" aria-hidden /> {c.phone}
                      </a>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-2.5 py-[9px] text-cime-text-primary">{c.city || <span className="text-neutral-300">—</span>}</td>
                  <td className="px-2.5 py-[9px]">
                    <span className={cn(
                      'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-[9px] text-xs font-semibold',
                      CATEGORY_HUE[c.contact_category],
                    )}>
                      {CONTACT_CATEGORY_LABELS[c.contact_category]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!isLoading && visible.length > 0 ? (
          <div className="border-t border-neutral-100 px-[18px] py-2.5 text-xs text-neutral-400">
            {visible.length} contact{visible.length > 1 ? 's' : ''}
          </div>
        ) : null}
        </div>
      </div>

      {createOpen && (
        <ContactEditModal
          onClose={() => setCreateOpen(false)}
          onSaved={(c) => navigate(`/contacts/${c.id}`)}
        />
      )}
    </>
  );
}

function CategoryDropdown({
  selected, onChange,
}: { selected: ContactCategory[]; onChange: (v: ContactCategory[]) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-9 items-center gap-[7px] rounded-full border px-3 text-[13px] font-medium transition',
          selected.length
            ? 'border-cime-primary bg-[rgba(4,52,41,0.07)] text-cime-primary'
            : 'border-neutral-200 bg-white text-cime-text-muted hover:border-neutral-400 hover:text-cime-text-primary',
        )}
      >
        Catégorie
        {selected.length > 0 && (
          <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-cime-primary px-[5px] text-[11px] font-bold text-white">
            {selected.length}
          </span>
        )}
        <FaChevronDown className="h-2.5 w-2.5" aria-hidden />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-neutral-200 rounded-cime shadow-lg py-1 min-w-44">
            {CATEGORIES.map((c) => {
              const checked = selected.includes(c);
              return (
                <label key={c} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-neutral-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange(checked ? selected.filter((v) => v !== c) : [...selected, c])
                    }
                    className="w-3.5 h-3.5"
                  />
                  <span>{CONTACT_CATEGORY_LABELS[c]}</span>
                </label>
              );
            })}
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full text-left px-3 py-1.5 text-xs text-cime-text-muted hover:bg-neutral-50 border-t border-neutral-100 mt-1"
              >
                Effacer
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
